import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

interface LedgerBalance { code: string; naam: string; soort: string; spoor: string; saldo: number; }

const SALDO_TOON: Record<string, string> = {
  belastingdienst: 'border-orange-200 bg-orange-50',
  allianz_rc: 'border-mfb-blue/30 bg-mfb-blue/5',
  foursure_opbrengst: 'border-emerald-200 bg-emerald-50',
  collectief_te_betalen: 'border-mfb-blue/30 bg-mfb-blue/5',
  lid_vordering: 'border-amber-200 bg-amber-50',
};

const SOORT_TOON: Record<string, string> = {
  Premie: 'border-mfb-blue/30 bg-mfb-blue/5',
  Servicefee: 'border-emerald-200 bg-emerald-50',
  Schade: 'border-red-200 bg-red-50',
  Assurantiebelasting: 'border-orange-200 bg-orange-50',
  Btw: 'border-amber-200 bg-amber-50',
  'Teruggaaf IPT': 'border-mfb-blue/30 bg-mfb-blue/5',
};

export const AdminBoekstukkenPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [data, setData] = useState<any>(null);
  const [colls, setColls] = useState<any[]>([]);
  const [coll, setColl] = useState('');
  const [ledger, setLedger] = useState<LedgerBalance[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = coll ? `${API_BASE_URL}/api/admin/boekstukken?collectivity_id=${coll}` : `${API_BASE_URL}/api/admin/boekstukken`;
      const [b, c, l] = await Promise.all([
        fetch(url, { headers }).then((r) => (r.ok ? r.json() : { regels: [], samenvatting: {}, aantal: 0 })),
        colls.length ? Promise.resolve(colls) : fetch(`${API_BASE_URL}/api/admin/collectivities`, { headers }).then((r) => (r.ok ? r.json() : [])),
        fetch(coll ? `${API_BASE_URL}/api/settlement/ledger?collectivity_id=${coll}` : `${API_BASE_URL}/api/settlement/ledger`, { headers }).then((r) => (r.ok ? r.json() : [])),
      ]);
      setData(b); setColls(c); setLedger(l);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, coll]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const exporteer = async () => {
    const url = coll ? `${API_BASE_URL}/api/admin/boekstukken/export?collectivity_id=${coll}` : `${API_BASE_URL}/api/admin/boekstukken/export`;
    const r = await fetch(url, { headers });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'boekstukken.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const samenvatting = data?.samenvatting || {};

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Boekstukken</h1>
          <p className="text-sm text-slate-500">Boekhoud-klare regels (Premie, Servicefee, Schade) uit het grootboek — klaar voor export naar Moneybird.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={coll} onChange={(e) => setColl(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm">
            <option value="">Alle collectiviteiten</option>
            {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={exporteer} className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white rounded-lg text-sm font-semibold transition shadow-sm">
            ↓ Exporteer CSV
          </button>
        </div>
      </div>

      {/* Totalen per soort */}
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-3">Totalen per soort</h2>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
        {['Premie', 'Servicefee', 'Schade', 'Assurantiebelasting', 'Btw', 'Teruggaaf IPT'].map((s) => (
          <div key={s} className={`rounded-2xl border p-4 ${SOORT_TOON[s] || 'border-slate-200 bg-slate-50'}`}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{s}</p>
            <p className="text-lg font-bold text-slate-800 mt-1 tabular-nums">{fmt(samenvatting[s] || 0)}</p>
          </div>
        ))}
      </div>

      {/* Grootboeksaldi — de optelling van de regels hieronder, per grootboekrekening */}
      {ledger.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-3">Grootboeksaldi</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {ledger.map((a) => (
              <div key={a.code} className={`rounded-2xl border p-4 ${SALDO_TOON[a.soort] || 'border-slate-200 bg-slate-50'}`}>
                <p className="text-xs uppercase tracking-wide text-slate-500">{a.spoor}</p>
                <p className="text-lg font-bold text-slate-800 mt-1 tabular-nums">{fmt(a.saldo)}</p>
                <p className="text-xs text-slate-500 mt-1">{a.naam}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-3">Boekingsregels</h2>
      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : !data || data.aantal === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          Nog geen boekstukken. Draai een afrekening of verwerk een schade.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-200 text-sm text-slate-500">{data.aantal} regels</div>
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Datum', 'Soort', 'Grootboek', 'Belastingcode', 'Debet', 'Credit', 'Lid', 'Omschrijving'].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.regels.map((r: any, i: number) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{new Date(r.datum).toLocaleDateString('nl-NL')}</td>
                  <td className="px-4 py-2 font-medium text-mfb-blue whitespace-nowrap">{r.soort}</td>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{r.grootboek} · {r.grootboeknaam}</td>
                  <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{r.belastingcode || '—'}</td>
                  <td className="px-4 py-2 text-slate-700 text-right">{r.debet ? fmt(r.debet) : ''}</td>
                  <td className="px-4 py-2 text-slate-700 text-right">{r.credit ? fmt(r.credit) : ''}</td>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{r.lid}</td>
                  <td className="px-4 py-2 text-slate-500">{r.omschrijving}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </Layout>
  );
};

export default AdminBoekstukkenPage;
